/**
 * Cognito User Pool 認証コンテキスト。
 *
 * - amazon-cognito-identity-js を用いて USER_PASSWORD_AUTH (Password Authentication)
 *   フローでログインする。
 * - ログイン成功時に取得した ID トークンを React state に保持し、API クライアントが
 *   `Authorization: Bearer <idToken>` で利用する。
 * - リフレッシュトークンが localStorage に永続化されるため、ページ再読み込みでも
 *   セッションが復元される (CognitoUserPool.getCurrentUser() で確認)。
 * - アカウントは管理者招待でのみ作成される (自己登録は無効。UserPool は AdminCreateUserOnly)。
 * - 管理者招待ユーザ (FORCE_CHANGE_PASSWORD): 初期パスワードでのログインは受け付け、
 *   `isNewPasswordRequired` が true になる。App 側が新パスワード設定画面を表示し、
 *   `completeNewPassword` 成功でそのままログイン済みになる。
 */
import {
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { AppConfig } from '../config';

export interface AuthState {
  /** 認証済みかどうか */
  isAuthenticated: boolean;
  /** 認証中の判定がまだ完了していない (初期化中)。trueの間はローディング表示する */
  isLoading: boolean;
  /** ログインユーザの email (sub から見つからない場合は username が入る) */
  username: string | null;
  /** API 呼び出し時に Bearer に乗せる ID トークン (期限切れの場合は再取得済み) */
  getIdToken: () => Promise<string>;
  /** 初期パスワードでのログイン後、新パスワードの設定が必要な状態か */
  isNewPasswordRequired: boolean;
  /** ログイン処理 (初期パスワードの場合は resolve し isNewPasswordRequired が true になる) */
  signIn: (email: string, password: string) => Promise<void>;
  /** 初期パスワードの変更を完了し、そのままログイン状態にする */
  completeNewPassword: (newPassword: string) => Promise<void>;
  /** 新パスワード設定を中断してログイン画面へ戻す */
  cancelNewPassword: () => void;
  /** ログアウト処理 */
  signOut: () => void;
  /** パスワードのセルフリセット (1): リセットコードをメールへ送信する */
  forgotPassword: (email: string) => Promise<void>;
  /** パスワードのセルフリセット (2): 受信したコードと新パスワードで再設定する */
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  /** 直近のエラーメッセージ */
  error: string | null;
}

const AuthContext = createContext<AuthState | null>(null);

interface AuthProviderProps {
  config: AppConfig;
  children: ReactNode;
}

export function AuthProvider({ config, children }: AuthProviderProps): React.JSX.Element {
  // CognitoUserPool は config から構築。再初期化を防ぐため useMemo で固定化する。
  const userPool = useMemo(
    () =>
      new CognitoUserPool({
        UserPoolId: config.userPoolId,
        ClientId: config.userPoolClientId,
      }),
    [config.userPoolId, config.userPoolClientId],
  );

  const [currentUser, setCurrentUser] = useState<CognitoUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [username, setUsername] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 初期パスワードでログインし、新パスワード設定待ちの CognitoUser (チャレンジのセッションを保持)
  const [challengeUser, setChallengeUser] = useState<CognitoUser | null>(null);

  // 初期化: localStorage に保存されている既存セッションを復元する。
  useEffect(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      setIsLoading(false);
      return;
    }
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        setCurrentUser(null);
        setUsername(null);
      } else {
        setCurrentUser(cognitoUser);
        setUsername(extractUsername(session));
      }
      setIsLoading(false);
    });
  }, [userPool]);

  const signIn = useCallback(
    (email: string, password: string): Promise<void> => {
      setError(null);
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      return new Promise<void>((resolve, reject) => {
        cognitoUser.authenticateUser(authDetails, {
          onSuccess: (session) => {
            setCurrentUser(cognitoUser);
            setUsername(extractUsername(session));
            resolve();
          },
          onFailure: (err: Error) => {
            const message = err.message || 'ログインに失敗しました';
            setError(message);
            reject(err);
          },
          newPasswordRequired: () => {
            // 管理者招待ユーザの初期パスワード (FORCE_CHANGE_PASSWORD)。
            // ログイン自体は成功扱いとし、新パスワード設定画面へ誘導する。
            // 同一の CognitoUser でチャレンジに応答する必要があるため保持する。
            setChallengeUser(cognitoUser);
            resolve();
          },
        });
      });
    },
    [userPool],
  );

  // 初期パスワードの変更を完了する。成功するとセッションが確立し、ログイン済みになる。
  // email / email_verified は変更不可の属性なので、必須属性としては何も渡さない。
  const completeNewPassword = useCallback(
    (newPassword: string): Promise<void> => {
      setError(null);
      if (!challengeUser) {
        const message = '初期パスワードでのログインからやり直してください。';
        setError(message);
        return Promise.reject(new Error(message));
      }
      return new Promise<void>((resolve, reject) => {
        challengeUser.completeNewPasswordChallenge(
          newPassword,
          {},
          {
            onSuccess: (session) => {
              setCurrentUser(challengeUser);
              setUsername(extractUsername(session));
              setChallengeUser(null);
              resolve();
            },
            onFailure: (err: Error) => {
              const message = err.message || 'パスワードの変更に失敗しました';
              setError(message);
              reject(err);
            },
          },
        );
      });
    },
    [challengeUser],
  );

  const cancelNewPassword = useCallback(() => {
    setChallengeUser(null);
    setError(null);
  }, []);

  const signOut = useCallback(() => {
    if (currentUser) {
      currentUser.signOut();
    }
    setChallengeUser(null);
    setCurrentUser(null);
    setUsername(null);
    setError(null);
  }, [currentUser]);

  // パスワードのセルフリセット (1): リセットコードをメールへ送信する。
  // Cognito の forgotPassword フローを利用する (AccountRecovery=EMAIL_ONLY)。
  const forgotPassword = useCallback(
    (email: string): Promise<void> => {
      setError(null);
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      return new Promise<void>((resolve, reject) => {
        cognitoUser.forgotPassword({
          onSuccess: () => {
            resolve();
          },
          onFailure: (err: Error) => {
            const message = err.message || 'パスワードリセットコードの送信に失敗しました';
            setError(message);
            reject(err);
          },
        });
      });
    },
    [userPool],
  );

  // パスワードのセルフリセット (2): 受信したコードと新パスワードでパスワードを再設定する。
  const confirmForgotPassword = useCallback(
    (email: string, code: string, newPassword: string): Promise<void> => {
      setError(null);
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      return new Promise<void>((resolve, reject) => {
        cognitoUser.confirmPassword(code, newPassword, {
          onSuccess: () => {
            resolve();
          },
          onFailure: (err: Error) => {
            const message = err.message || 'パスワードの再設定に失敗しました';
            setError(message);
            reject(err);
          },
        });
      });
    },
    [userPool],
  );

  // 有効な ID トークンを取得する。期限切れなら refreshToken で更新する。
  const getIdToken = useCallback((): Promise<string> => {
    return new Promise((resolve, reject) => {
      const user = currentUser ?? userPool.getCurrentUser();
      if (!user) {
        reject(new Error('Not authenticated'));
        return;
      }
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          reject(err ?? new Error('Failed to fetch session'));
          return;
        }
        // getSession 自体が refreshToken による自動更新を実施する。
        resolve(session.getIdToken().getJwtToken());
      });
    });
  }, [currentUser, userPool]);

  const value: AuthState = useMemo(
    () => ({
      isAuthenticated: currentUser !== null,
      isLoading,
      username,
      getIdToken,
      isNewPasswordRequired: challengeUser !== null,
      signIn,
      completeNewPassword,
      cancelNewPassword,
      signOut,
      forgotPassword,
      confirmForgotPassword,
      error,
    }),
    [
      currentUser,
      isLoading,
      username,
      getIdToken,
      challengeUser,
      signIn,
      completeNewPassword,
      cancelNewPassword,
      signOut,
      forgotPassword,
      confirmForgotPassword,
      error,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Cognito の ID トークンから email (なければ username) を取り出す。 */
function extractUsername(session: CognitoUserSession): string {
  const payload = session.getIdToken().decodePayload();
  return (payload['email'] as string) ?? (payload['cognito:username'] as string) ?? '';
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
